import { useMemo } from "react";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetTaxYearsQuery } from "v2/store/taxSetupApi";
import { useOnboardingProgress } from "v2/utils/useOnboardingProgress";
import {
  useGetActionItemsByDeadlineQuery,
  type ActionItemsByDeadline,
  type DeadlineActionItem,
  type PendingSection,
} from "store/apis/actionItems";
import { pluralize } from "utils/pluralize";
import { CardFadeOverlay } from "./CardFadeOverlay";
import { TasksCardEmptyState } from "./TasksCardEmptyState";
import { buildBucketSubtitle } from "./buildBucketSubtitle";
import { PendingTaskRow, type PendingRow } from "./PendingTaskRow";
import { ProfileSetupRow } from "./ProfileSetupRow";
import {
  PENDING_SECTION_CONFIG,
  PENDING_SECTION_ORDER,
  type ApiPendingSectionKey,
} from "./pendingSectionConfig";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";

const INVOICES_DUE_KEY: ApiPendingSectionKey = "INVOICES_DUE";
const CART_PAYMENT_PENDING_KEY: ApiPendingSectionKey = "CART_PAYMENT_PENDING";
const FULL_PROFILE_PROGRESS = 100;

const buildRow = (
  key: ApiPendingSectionKey,
  section: PendingSection
): PendingRow => {
  const config = PENDING_SECTION_CONFIG[key];
  const count = section.items_count;
  const first = section.items[0];

  const title =
    config.staticTitle ??
    pluralize(count, config.titleSingular ?? "", config.titlePlural ?? "");

  const subtitle = config.useBucketSubtitle
    ? buildBucketSubtitle(section.items)
    : config.staticSubtitle ?? "";

  return {
    key,
    rowId: key,
    behavior: config.behavior,
    accent: config.accent,
    title,
    subtitle,
    actionLabel: config.actionLabel,
    tag: config.tag,
    ids: section.items.flatMap((item) => item.ids),
    entityId: first?.entity_id ?? null,
    stateId: first?.state_id ?? null,
    count,
  };
};

// Invoices are entity-level: one row per entity. Dedupe by entity in case the
// endpoint returns the same entity more than once (merge count + ids).
const buildInvoiceRows = (section: PendingSection): PendingRow[] => {
  const config = PENDING_SECTION_CONFIG[INVOICES_DUE_KEY];
  const byEntity = new Map<string, DeadlineActionItem>();

  section.items.forEach((item) => {
    const entityKey = item.entity_id ?? item.ids.join(",");
    const existing = byEntity.get(entityKey);

    if (!existing) {
      byEntity.set(entityKey, { ...item });
      return;
    }

    existing.count += item.count;
    existing.ids = [...existing.ids, ...item.ids];
  });

  return Array.from(byEntity.entries()).map(([entityKey, item]) => {
    const countLabel = pluralize(item.count, "invoice due", "invoices due");
    const title = item.entity_name
      ? `${countLabel} for ${item.entity_name}`
      : countLabel;

    return {
      key: INVOICES_DUE_KEY,
      rowId: entityKey,
      behavior: config.behavior,
      accent: config.accent,
      title,
      subtitle: config.staticSubtitle ?? "",
      actionLabel: config.actionLabel,
      ids: item.ids,
      entityId: item.entity_id,
      stateId: item.state_id,
      count: item.count,
    };
  });
};

// Cart is a single row covering every entity with something in its cart. The
// endpoint only sends entity ids here, so names come from the group context.
const buildCartRow = (
  section: PendingSection,
  entityNameById: Record<string, string>
): PendingRow => {
  const row = buildRow(CART_PAYMENT_PENDING_KEY, section);

  const names = [
    ...row.ids.map((entityId) => entityNameById[entityId]),
    ...section.items.map((item) => item.entity_name),
  ];
  const entityNames = Array.from(new Set(names.filter(Boolean)));

  if (entityNames.length === 0) return row;

  return { ...row, title: `${row.title} for ${entityNames.join(", ")}` };
};

const buildRows = (
  data: ActionItemsByDeadline,
  entityNameById: Record<string, string>
): PendingRow[] => {
  // Render in the fixed FE order, keeping only sections that have items.
  return PENDING_SECTION_ORDER.flatMap((key) => {
    const section = data[key];
    if (!section || (section.items_count ?? 0) <= 0) return [];

    if (key === INVOICES_DUE_KEY) return buildInvoiceRows(section);

    if (key === CART_PAYMENT_PENDING_KEY) {
      return [buildCartRow(section, entityNameById)];
    }

    return [buildRow(key, section)];
  });
};

const PendingTasksSkeleton = () => (
  <div className="t-flex t-flex-col t-gap-6 t-p-6">
    {[0, 1, 2].map((row) => (
      <div key={row} className="t-flex t-flex-col t-gap-2">
        <Skeleton className="t-h-5 t-w-1/2" />
        <Skeleton className="t-h-4 t-w-3/4" />
      </div>
    ))}
  </div>
);

type PendingTasksCardProps = {
  entityIds: string[];
  isScopeReady: boolean;
};

export const PendingTasksCard = ({
  entityIds,
  isScopeReady,
}: PendingTasksCardProps) => {
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const { data, isLoading, isError } = useGetActionItemsByDeadlineQuery(
    { groupId, entityIds },
    { skip: !groupId || !isScopeReady }
  );

  const isV2Platform = useIsGroupV2Platform();

  const { data: taxYears } = useGetTaxYearsQuery(
    { groupId },
    { skip: !groupId }
  );

  const setupEntities = useMemo(
    () =>
      (taxYears?.entities ?? [])
        .filter(
          (entity) => entityIds.length === 0 || entityIds.includes(entity.uuid)
        )
        .map((entity) => ({
          id: entity.uuid,
          name: entity.name,
        })),
    [taxYears, entityIds]
  );

  const setupEntityIds = useMemo(
    () => setupEntities.map((entity) => entity.id),
    [setupEntities]
  );

  const { overallProgress, isProgressReady, probes } = useOnboardingProgress({
    groupId,
    entityIds: setupEntityIds,
  });

  const entityNameById = Object.fromEntries(
    entities.map((entity) => [entity.uuid, entity.name])
  );

  const rows = data ? buildRows(data, entityNameById) : [];

  const shouldShowProfileRow =
    isV2Platform &&
    setupEntities.length > 0 &&
    overallProgress < FULL_PROFILE_PROGRESS;

  let content = (
    <>
      <div className="hide-scrollbar t-h-full t-overflow-y-auto t-pb-6">
        {shouldShowProfileRow && (
          <ProfileSetupRow
            progress={overallProgress}
            isProgressReady={isProgressReady}
            entities={setupEntities}
          />
        )}

        {rows.map((row) => (
          <PendingTaskRow key={row.rowId} row={row} />
        ))}
      </div>
      <CardFadeOverlay />
    </>
  );

  if (isLoading) {
    content = <PendingTasksSkeleton />;
  }

  if (isError) {
    content = (
      <TasksCardEmptyState
        title="Couldn't load tasks"
        description="Something went wrong while loading your pending tasks. Please try again."
      />
    );
  }

  if (!isLoading && !isError && rows.length === 0 && !shouldShowProfileRow) {
    content = (
      <TasksCardEmptyState
        title="All caught up"
        description="No pending tasks right now. We'll let you know the moment something needs your attention."
      />
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      {probes}

      <div className="t-flex t-h-8 t-items-center">
        <div className="t-text-subtext-lg t-text-text-60">Pending on you</div>
      </div>
      <div className="t-relative t-h-[400px] t-overflow-hidden t-rounded-2xl t-border t-border-solid t-border-neutral-10 t-bg-white">
        {content}
      </div>
    </div>
  );
};
